export const SYSTEM_PROMPT = `
You are an AI specialized in generating interview questions tailored to a specific job description. 
Based on the provided job description:
{job_description}

0) Only generate questions when the human request for it in the human input.
1) Generate the number of unique and relevant interview questions specified by {no} or as stated by the Human in their input. These questions should assess the candidate's skills, experience, and suitability for the role.
2) Do not generate any question that is identical, similar in phrasing, or overlaps in meaning with the questions already generated. Each question must be entirely unique, addressing a different skill, experience, or responsibility from the job description, without repeating or rephrasing concepts covered in previous questions.
3) If there is a user input, please response to the user's input accordingly.
4) Use the provided chat history to maintain logical continuity in responses.
5) Do not include any personal information in the responses.
6) Do not generate content unrelated to job interviews or the context of assessing candidates.
7) Use the **most recent type of questions in the chat history** to infer the type if the user's request does not specify one (e.g., "Can I have 2 more questions" should generate the same type as the last set of questions in the chat history).
`;

export const generateJobInterviewQns = (
  systemPrompt,
  jobDescription,
  no = 5,
  isFormatInstruction = true,
  existingQnsConfig = null,
  inputConfig = null
) => {
  let systemPromptSet = systemPrompt;

  // Input for the request
  let input = {
    job_description: jobDescription,
    no: `${no}`,
  };

  // Set message log (1)
  if (existingQnsConfig) {
    if (
      existingQnsConfig?.messageLog &&
      existingQnsConfig?.messageLog.length > 0
    ) {
      input[existingQnsConfig?.field] = messageLogToReadableText(
        JSON.stringify(existingQnsConfig?.messageLog)
      );
    } else if (
      existingQnsConfig?.field &&
      existingQnsConfig?.messageLog.length === 0
    ) {
      input[existingQnsConfig?.field] = "";
    }

    systemPromptSet = addPlaceHolder2(
      systemPromptSet,
      existingQnsConfig?.field,
      // convertFieldToPrefix(existingQnsConfig?.field)
      "Chat History: "
    );
  }

  // Set format instructions (2)
  systemPromptSet = isFormatInstruction
    ? addFormatInstructions(systemPromptSet)
    : systemPrompt;

  // Set the user input (3)
  if (inputConfig) {
    // Add a user: placeholder
    systemPromptSet = addPlaceHolder(
      systemPromptSet,
      inputConfig?.field,
      "Human: ",
      true
    );
    input[inputConfig?.field] = inputConfig?.input;
  }

  return {
    input,
    model: "gpt-4o-mini",
    max_error_allowed: 2,
    system_prompt: systemPromptSet,
    is_structured_output: true,
    structure_output_config: [
      {
        name: "response",
        type: "str",
        defaultValue: "",
        description: "The bot responses to the user's input.",
      },
      {
        name: "interviewQuestions",
        type: "List[str]",
        defaultValue: "",
        description:
          "A list of unique and relevant interview questions for the job description.",
        nestedConfig: [],
      },
    ],
  };
};

export function parseHtmlToText(htmlString) {
  // Use DOMParser to parse the HTML string
  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlString, "text/html");

  // Extract the text content of the parsed document
  return doc.body.textContent || "";
}

const addFormatInstructions = (systemPrompt) => {
  if (!systemPrompt.includes("{format_instructions}")) {
    systemPrompt += "\n\n{format_instructions}";
  }
  return systemPrompt;
};

const generateInterviewQuestionString = (interviewQuestions) => {
  if (!interviewQuestions) return "";
  if (interviewQuestions.length === 0) return "";
  let questionString = "";
  interviewQuestions.forEach((question, index) => {
    questionString += `${index + 1}. ${question.question}\n`;
  });
  return questionString;
};

const addPlaceHolder = (
  systemPrompt,
  placeHolderName,
  prefix = "",
  isSameLine = false
) => {
  if (!systemPrompt.includes(`{${placeHolderName}}`)) {
    if (prefix === "") {
      systemPrompt += `\n\n{${placeHolderName}}`;
    } else {
      if (isSameLine) {
        systemPrompt += `\n\n${prefix} {${placeHolderName}}`;
      } else {
        systemPrompt += `\n\n${prefix}\n{${placeHolderName}}`;
      }
    }
  }
  return systemPrompt;
};

const addPlaceHolder2 = (
  systemPrompt,
  placeHolderName,
  prefix = "",
  isSameLine = false
) => {
  if (!systemPrompt.includes(`{${placeHolderName}}`)) {
    if (prefix === "") {
      systemPrompt += `\n\n{${placeHolderName}}`;
    } else {
      if (isSameLine) {
        systemPrompt += `\n\n${prefix} {${placeHolderName}}`;
      } else {
        systemPrompt += `\n\n${prefix}
        <START OF ${prefix}> {${placeHolderName}} <END OF ${prefix}>`;
      }
    }
  }
  return systemPrompt;
};

const convertFieldToPrefix = (field) => {
  // Split the field name by underscore and capitalize each word
  // First check if there is an underscore in the field name
  let header = "";
  if (field.includes("_")) {
    const words = field.split("_");
    const capitalizedWords = words.map((word) => {
      return word.charAt(0).toUpperCase() + word.slice(1);
    });
    header = capitalizedWords.join(" ");
  } else {
    return field.charAt(0).toUpperCase() + field.slice(1);
  }
  return `${header}: `;
};

function messageLogToReadableText(messageLog) {
  // Parse the JSON string into JavaScript objects
  const log = JSON.parse(messageLog);
  let readableText = "";

  log.forEach((entry) => {
    if (entry.type === "human") {
      readableText += `Human: ${entry.message}\n`;
    } else if (entry.type === "bot") {
      readableText += `Bot: ${entry.message}\n`;
      if (entry.data && entry.data.questions) {
        const questions = entry.data.questions.map((q) => `- ${q}`).join("\n");
        readableText += `Questions:\n${questions}\n`;
      }
    }
  });

  return readableText.trim();
}
